<template>
  <div>
    <div class="page-header page-header-hero">
      <!-- LCP image: real <img> so the browser preload scanner finds it immediately,
           without waiting for JS/Vue to boot. fetchpriority=high tells the browser
           to treat this as the most important resource on the page. -->
      <picture class="hero-picture">
        <source srcset="/img/beastmode/bg_white.webp" type="image/webp" media="(min-width: 992px)" />
        <source srcset="/img/beastmode/bg_dark.webp" type="image/webp" />
        <source srcset="/img/beastmode/bg_white.JPG" media="(min-width: 992px)" />
        <img
          src="/img/beastmode/bg_dark.JPG"
          alt="Professional cleaning service"
          class="hero-img"
          fetchpriority="high"
          decoding="async"
        />
      </picture>
      <!-- Dark gradient overlay for text legibility -->
      <div class="hero-overlay"></div>
      <div class="content-center hero-content">
        <div class="container">
          <p class="hero-eyebrow">Professional Cleaning Services</p>
          <h1 class="hero-title">We don't cut corners.<br>We clean them.</h1>
          <p class="hero-sub">Homes, AirBnBs, boats, RVs — done right, every time.<br>Serving Denison, Sherman, Pottsboro TX &amp; Durant OK.</p>
          <div class="hero-actions">
            <router-link to="/contactus" class="btn btn-primary btn-lg btn-round hero-cta">
              Get a Free Quote
            </router-link>
            <router-link to="/gallery" class="btn btn-outline-light btn-lg btn-round hero-secondary">
              See Our Work
            </router-link>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Services Section -->
    <div class="section section-services">
      <div class="container">
        <div class="section-header text-center">
          <h2 class="section-title">What We Clean</h2>
          <p class="section-subtitle">Every job gets our full attention — no shortcuts, no excuses.</p>
        </div>
        <div class="row services-grid">
          <div class="col-12 col-md-6 col-xl-4 mb-4" v-for="(service, index) in services" :key="index">
            <ServiceCard
              :title="service.cat.title"
              :price="service.cat.price"
              :description="service.cat.description"
              :image="service.cat.image"
              :details="service.details"
            />
          </div>
        </div>
      </div>
    </div>
    
    <div class="section section-team text-center">
      <div class="container">
        <h2 class="title">Meet the Team</h2>
        <div class="team">
          <div class="row">
            <div class="col" v-for="(operator, index) in operators" :key="index">
              <OperatorCard
                :firstname="operator.firstname"
                :lastname="operator.lastname"
                :title="operator.title"
                :description="operator.description"
                :image="operator.image"
                :facebook="operator.facebook"
                :instagram="operator.instagram"
                :twitter="operator.twitter"
                :linkedin="operator.linkedin"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="section section-contact-us text-center">
      <div class="container">
        <h2 class="title">Ready for a Spotless Space?</h2>
        <p class="description">Contact us for a free consultation and personalized quote.</p>
        <router-link to="/contactus" class="btn btn-primary btn-round" aria-label="Contact Beastmode Cleanup for a free quote">
          Get Your Free Quote
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { Button, FormGroupInput } from '@/components'
import ServiceCard from '@/components/ServiceCard'
import OperatorCard from '@/components/OperatorCard'
import axios from 'axios'

// Detect WebP support once at module level
function supportsWebP() {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1; canvas.height = 1
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
  } catch (e) { return false }
}

export default {
  name: 'landing',
  bodyClass: 'landing-page',
  metaInfo: {
    title: 'Beastmode Cleanup | Professional Cleaning Services',
    meta: [
      { name: 'description', content: 'Beastmode Cleanup provides professional home, AirBnB, boat and RV cleaning services in Denison, Sherman, Pottsboro TX and Durant OK. Reliable, thorough, and affordable. Request a free quote today!' },
      { name: 'robots', content: 'index, follow' },
      { property: 'og:title', content: 'Beastmode Cleanup | Professional Cleaning Services' },
      { property: 'og:description', content: 'Beastmode Cleanup provides professional home, AirBnB, and recreational vehicle cleaning services. Request a free quote today!' },
      { property: 'og:url', content: 'https://www.beastmodecleanup.com/' },
      { property: 'og:type', content: 'website' },
    ],
    link: [
      { rel: 'canonical', href: 'https://www.beastmodecleanup.com/' },
      // Hero image preloaded in index.html (before JS) for best LCP
      // Prefetch the two pages users most commonly visit after the homepage
      { rel: 'prefetch', as: 'script', href: '/js/pg-gallery.js' },
      { rel: 'prefetch', as: 'script', href: '/js/pg-contact.js' },
    ]
  },
  components: {
    [Button.name]: Button,
    [FormGroupInput.name]: FormGroupInput,
    ServiceCard,
    OperatorCard,
  },
  data() {
    return {
      headers: {
        'Content-Type': 'application/json'
      },
      services: [],
      operators: [],
      isDesktop: false,
      // WebP with JPEG fallback — browser picks best supported format
      defaultImage: supportsWebP() ? 'img/beastmode/bg_dark.webp' : 'img/beastmode/bg_dark.JPG',
      desktopImage: supportsWebP() ? 'img/beastmode/bg_white.webp' : 'img/beastmode/bg_white.JPG',
    };
  },
  computed: {
    currentBackgroundImage() {
      return this.isDesktop ? this.desktopImage : this.defaultImage;
    }
  },
  mounted() {
    this.getServices();
    this.getOperators();
    this.updateScreenSize();
    window.addEventListener('resize', this.updateScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScreenSize);
  },
  methods: {
    updateScreenSize() {
      this.isDesktop = window.innerWidth >= 992;
    },
    async getServices() {
      try {
        const { data: response } = await axios.get('/getcategories', {
          headers: this.headers
        });
        this.services = response;
      } catch (err) {
        //console.log(err);
      }
    },
    async getOperators() {
      try {
        const { data: response } = await axios.get('/getoperators', {
          headers: this.headers
        });
        this.operators = response;
      } catch (err) {
        //console.log(err);
      }
    },
    async insertImages() {
      const params = {
        type: 'RV',
        name: 'rv_1',
        number: 6
      };
      try {
        const { data: response } = await axios.post('/newimages', params, {
          headers: this.headers
        });
      } catch (err) {
        //console.log(err);
      }
    },
  },
};
</script>

<style scoped>
/* ── Hero ── */
.page-header-hero {
  min-height: 90vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

/* LCP image fills the hero slot exactly like the old CSS background */
.hero-picture {
  position: absolute;
  inset: 0;
  z-index: 0;
}
.hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(10, 0, 0, 0.75) 0%,
    rgba(10, 0, 0, 0.50) 60%,
    rgba(10, 0, 0, 0.15) 100%
  );
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  width: 100%;
  text-align: left;
  padding: 120px 0 80px;
}

.hero-eyebrow {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;
}

.hero-title {
  font-size: clamp(2.2rem, 5vw, 3.8rem);
  font-weight: 800;
  color: #ffffff;
  line-height: 1.15;
  margin-bottom: 1.25rem;
  max-width: 700px;
}

.hero-sub {
  font-size: 1.35rem;
  color: rgba(255, 255, 255, 0.82);
  margin-bottom: 2.25rem;
  max-width: 480px;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.hero-cta {
  font-weight: 700;
  padding: 14px 36px;
  font-size: 1rem;
}

.hero-secondary {
  font-weight: 600;
  padding: 13px 32px;
  font-size: 1rem;
  border-color: rgba(255, 255, 255, 0.6);
  color: #ffffff;
}

.hero-secondary:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: #ffffff;
}

@media (max-width: 768px) {
  .hero-content {
    text-align: center;
    padding: 100px 0 60px;
  }
  .hero-title,
  .hero-sub {
    max-width: 100%;
  }
  .hero-actions {
    justify-content: center;
  }
}

/* ── Services Section ── */
.section-services {
  padding: 80px 0;
  background: #f9f9f9;
}

.section-header {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: clamp(1.7rem, 3vw, 2.4rem);
  font-weight: 800;
  color: #111111;
  margin-bottom: 0.6rem;
}

.section-subtitle {
  font-size: 1rem;
  color: #666;
  max-width: 480px;
  margin: 0 auto;
  line-height: 1.6;
}

.services-grid {
  align-items: stretch;
}

.services-grid > [class*="col-"] {
  display: flex;
}

.services-grid > [class*="col-"] > * {
  width: 100%;
}
</style>